import { supabase } from '@/integrations/supabase/client';
import { ProjectSize, ProjectSizeMetrics } from '../types/scanConfig.types';

/**
 * Detect database size based on table count and estimated rows
 */
export async function detectDatabaseSize(): Promise<'small' | 'medium' | 'large'> {
  try {
    // Get all tables from public schema
    const { data: tables, error } = await supabase.rpc('get_public_tables' as any);
    
    if (error || !tables) {
      console.warn('Could not detect database size, defaulting to small');
      return 'small';
    }
    
    const tableCount = Array.isArray(tables) ? tables.length : 0;
    
    // Estimate total rows (sampling approach)
    let estimatedTotalRows = 0;
    const sampleTables = Array.isArray(tables) ? tables.slice(0, 5) : [];
    
    for (const tableData of sampleTables) {
      const tableName = typeof tableData === 'string' ? tableData : tableData.table_name;
      if (!tableName) continue;
      
      try {
        const { count } = await supabase
          .from(tableName as any)
          .select('*', { count: 'exact', head: true });
        
        estimatedTotalRows += count || 0;
      } catch (err) {
        // Skip tables we can't access
        continue;
      }
    }
    
    // Extrapolate to all tables
    const avgRowsPerTable = estimatedTotalRows / Math.max(sampleTables.length, 1);
    const totalEstimatedRows = avgRowsPerTable * tableCount;
    
    console.log(`📊 Database: ${tableCount} tables, ~${Math.round(totalEstimatedRows)} rows`);
    
    // Classification
    if (tableCount > 50 || totalEstimatedRows > 10000) {
      return 'large';
    } else if (tableCount > 10 || totalEstimatedRows > 1000) {
      return 'medium';
    }
    return 'small';
    
  } catch (error) {
    console.error('Error detecting database size:', error);
    return 'small';
  }
}

/**
 * Estimate source file count (rough approximation without edge function)
 */
export function estimateSourceFileCount(): number {
  // Try to navigate to routes and see which exist (this is approximate)
  // In practice, we'll use the actual route list length
  const allLinks = document.querySelectorAll('a[href^="/"]');
  const uniqueRoutes = new Set<string>();
  
  allLinks.forEach(link => {
    const href = link.getAttribute('href');
    if (href && href.startsWith('/') && !href.includes('#')) {
      uniqueRoutes.add(href.split('?')[0]);
    }
  });
  
  const detectedRoutes = uniqueRoutes.size;
  
  // Rough estimation: 3-5 files per route on average
  const estimatedFiles = detectedRoutes * 4;
  
  console.log(`📊 Estimated ${detectedRoutes} routes → ~${estimatedFiles} source files`);
  
  return estimatedFiles;
}

/**
 * Get all available routes from the application
 */
export function getAllRoutes(): string[] {
  const routes = new Set<string>();
  
  // 1. Find all <a> tags with href starting with "/"
  const allLinks = document.querySelectorAll('a[href^="/"]');
  allLinks.forEach(link => {
    const href = link.getAttribute('href');
    if (href && href.startsWith('/') && !href.includes('#')) {
      routes.add(href.split('?')[0]);
    }
  });
  
  // 2. Find all React Router Link components (they render as <a> but might have data attributes)
  const reactLinks = document.querySelectorAll('[data-discover-link], [href^="/"]');
  reactLinks.forEach(link => {
    const href = link.getAttribute('href');
    if (href && href.startsWith('/') && !href.includes('#')) {
      routes.add(href.split('?')[0]);
    }
  });
  
  // 3. Add common routes that should always be scanned
  const commonRoutes = [
    '/',
    '/dashboard',
    '/profile',
    '/settings',
    '/moderator',
    '/admin',
    '/supabase-monitoring',
    '/qa/audit',
    '/ai-discovery',
    '/ai-learning',
    '/chat-hub',
    '/discussion/general',
    '/memory-center',
    '/personal-ai',
    '/research/library',
    '/ai-beacon/register',
    '/ai-discovery-observatory',
    '/ai-social-dynamics',
    '/ai-meta-learning',
    '/real-ai-integration',
    '/ai-feedback-processor',
    '/lovable-tools-builder'
  ];
  commonRoutes.forEach(route => routes.add(route));
  
  const routesArray = Array.from(routes).sort();
  console.log(`🔍 getAllRoutes(): Found ${routesArray.length} routes:`, routesArray);
  
  return routesArray;
}

/**
 * Get prioritized routes based on likelihood of containing mock data
 * High-risk routes are scanned first for efficiency
 */
export function getPrioritizedRoutes(): string[] {
  const allRoutes = getAllRoutes();
  
  // Define priority tiers (high risk = more likely to have mock data)
  const priorityTiers = {
    critical: [
      '/dashboard', '/admin', '/moderator', '/analytics', '/stats',
      '/supabase-monitoring', '/monitoring', '/metrics'
    ],
    high: [
      '/profile', '/user', '/users', '/account', '/settings',
      '/orders', '/products', '/inventory', '/sales', '/customers',
      '/billing', '/payments', '/transactions', '/invoices'
    ],
    medium: [
      '/about', '/contact', '/team', '/pricing', '/features',
      '/demo', '/test', '/example', '/sample', '/playground'
    ],
    low: [
      '/', '/home', '/login', '/signup', '/auth', '/forgot-password',
      '/privacy', '/terms', '/docs', '/help', '/faq'
    ]
  };
  
  // Score each route
  const scoredRoutes = allRoutes.map(route => {
    let score = 0;
    const routeLower = route.toLowerCase();
    
    // Critical tier (100 points)
    if (priorityTiers.critical.some(pattern => routeLower.includes(pattern))) {
      score += 100;
    }
    
    // High tier (75 points)
    if (priorityTiers.high.some(pattern => routeLower.includes(pattern))) {
      score += 75;
    }
    
    // Medium tier (50 points)
    if (priorityTiers.medium.some(pattern => routeLower.includes(pattern))) {
      score += 50;
    }
    
    // Low tier (25 points)
    if (priorityTiers.low.some(pattern => routeLower.includes(pattern))) {
      score += 25;
    }
    
    // Bonus points for specific keywords
    if (/test|demo|mock|sample|example|playground/.test(routeLower)) {
      score += 30; // Likely contains test data!
    }
    if (/admin|moderator|dashboard/.test(routeLower)) {
      score += 20; // Admin pages often show stats
    }
    if (/data|table|list|grid|chart/.test(routeLower)) {
      score += 15; // Data display pages
    }
    
    // Default score for unmatched routes
    if (score === 0) score = 10;
    
    return { route, score };
  });
  
  // Sort by score (descending) and return routes only
  const prioritized = scoredRoutes
    .sort((a, b) => b.score - a.score)
    .map(item => item.route);
  
  console.log(`🎯 Prioritized ${prioritized.length} routes by mock data risk`);
  console.log(`   Top 5 high-risk routes:`, prioritized.slice(0, 5));
  
  return prioritized;
}

/**
 * Main function: Detect project size based on all metrics
 */
export async function detectProjectSize(): Promise<ProjectSizeMetrics> {
  console.log('🔍 Detecting project size...');
  
  // Get metrics
  const routes = getAllRoutes();
  const routeCount = routes.length;
  const databaseSize = await detectDatabaseSize();
  const estimatedSourceFiles = estimateSourceFileCount();
  
  // Scoring system (0-6 points)
  let score = 0;
  
  // Routes (0-2 points)
  if (routeCount > 50) score += 2;
  else if (routeCount > 20) score += 1;
  
  // Database (0-2 points)
  if (databaseSize === 'large') score += 2;
  else if (databaseSize === 'medium') score += 1;
  
  // Source files (0-2 points)
  if (estimatedSourceFiles > 500) score += 2;
  else if (estimatedSourceFiles > 200) score += 1;
  
  // Classify project size
  let size: ProjectSize;
  if (score >= 4) {
    size = 'large';
  } else if (score >= 2) {
    size = 'medium';
  } else {
    size = 'small';
  }
  
  const metrics: ProjectSizeMetrics = {
    routeCount,
    databaseSize,
    estimatedSourceFiles,
    score,
    size
  };
  
  console.log(`📊 Project Size Detection:`, metrics);
  
  return metrics;
}

/**
 * Get recommended scan strategy message for UI
 */
export function getProjectSizeRecommendation(size: ProjectSize): {
  title: string;
  description: string;
  recommendations: string[];
} {
  switch (size) {
    case 'large':
      return {
        title: '⚠️ Large Project Detected',
        description: 'Your project has 50+ routes or 50+ database tables.',
        recommendations: [
          '✅ Recommended: Use "Quick Scan This Page" for targeted scanning',
          '⚠️ Warning: "Deep Scan" may take 5-10 minutes',
          '💡 Tip: Use "Scan Database" separately for better performance',
          '🎯 Best: Focus scans on specific critical pages'
        ]
      };
    
    case 'medium':
      return {
        title: '📊 Medium Project Detected',
        description: 'Your project has 20-50 routes or 10-50 database tables.',
        recommendations: [
          '✅ Recommended: "Deep Scan" with batch processing (2-3 minutes)',
          '💡 Tip: "Scan All Routes" will process in batches automatically',
          '⚡ Alternative: Use "Quick Scan" on important pages'
        ]
      };
    
    default: // small
      return {
        title: '✅ Small Project',
        description: 'Your project is optimized for fast scanning.',
        recommendations: [
          '✅ All scan types are fast and safe',
          '🚀 "Deep Scan" recommended for comprehensive results',
          '⚡ Typical scan time: 30-60 seconds'
        ]
      };
  }
}
